import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtAgo } from '@quadro/domain';
import { Avatar } from '../../components/Avatar';
import * as api from '../../data/api';
import { useAction } from '../../data/queries';
import { useWorkspace } from '../../state/workspace';

/** Texto com as @menções destacadas (só as que viraram menção de verdade). */
function Body({ c }: { c: api.Comment }) {
  const ws = useWorkspace();
  const names = c.mentions.map(id => ws.people.find(p => p.id === id)?.name).filter(Boolean) as string[];
  if (!names.length) return <>{c.body}</>;
  const re = new RegExp(`(${names.map(n => `@${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).join('|')})`, 'g');
  return <>{c.body.split(re).map((part, i) => (i % 2 ? <span key={i} className="mention">{part}</span> : <Fragment key={i}>{part}</Fragment>))}</>;
}

/** Caixa de texto com @menção: ao digitar @, sugere pessoas da área (setas e Enter para escolher). */
function Composer({ initial = '', initialMentions = [], onSubmit, onCancel, label }: {
  initial?: string; initialMentions?: string[]; onSubmit: (body: string, mentions: string[]) => Promise<unknown>; onCancel?: () => void; label: string;
}) {
  const ws = useWorkspace();
  const [text, setText] = useState(initial);
  const [mentions, setMentions] = useState<string[]>(initialMentions);
  const [query, setQuery] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  // Cursor depois do nome escolhido: posicionado no mesmo render que insere o nome (sem esperar um frame,
  // senão o que a pessoa digitar logo em seguida cai no lugar errado).
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current === null || !ref.current) return;
    ref.current.focus();
    ref.current.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [text]);
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const options = query === null ? [] : ws.members.filter(p => norm(p.name).includes(norm(query))).slice(0, 6);

  function onInput(v: string) {
    setText(v);
    const caret = ref.current?.selectionStart ?? v.length;
    const m = /(^|\s)@([\wÀ-ÿ]*)$/.exec(v.slice(0, caret));
    setQuery(m ? m[2] : null);
    setPick(0);
  }
  function choose(p: { id: string; name: string }) {
    const el = ref.current!, at = el.selectionStart ?? text.length;
    const start = text.slice(0, at).lastIndexOf('@');
    const next = `${text.slice(0, start)}@${p.name} ${text.slice(at)}`;
    caret.current = start + p.name.length + 2;
    setText(next);
    setMentions(m => [...new Set([...m, p.id])]);
    setQuery(null);
  }
  async function submit() {
    const body = text.trim();
    if (!body || busy) return;
    // Só fica a menção cujo "@Nome" continua no texto.
    const kept = mentions.filter(id => { const n = ws.people.find(p => p.id === id)?.name; return n && body.includes(`@${n}`); });
    setBusy(true);
    try { await onSubmit(body, kept); setText(''); setMentions([]); } finally { setBusy(false); }
  }

  return (
    <div className="cm-compose">
      <textarea ref={ref} rows={2} aria-label={label} placeholder="Escreva um comentário. Use @ para avisar alguém." value={text}
        onChange={e => onInput(e.target.value)}
        onKeyDown={e => {
          if (options.length) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setPick(i => (i + 1) % options.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setPick(i => (i - 1 + options.length) % options.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(options[pick]); return; }
            if (e.key === 'Escape') { e.stopPropagation(); setQuery(null); return; }
          }
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void submit(); }
        }} />
      {options.length > 0 && (
        <ul className="cm-suggest" role="listbox" aria-label="Pessoas">
          {options.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === pick}>
              <button type="button" onMouseDown={e => { e.preventDefault(); choose(p); }}><Avatar id={p.id} />{p.name}</button>
            </li>
          ))}
        </ul>
      )}
      <div className="cm-acts">
        <span className="muted">Ctrl+Enter envia</span>
        <span className="grow" />
        {onCancel && <button type="button" className="btn" onClick={onCancel}>Cancelar</button>}
        <button type="button" className="btn primary" disabled={!text.trim() || busy} onClick={submit}>{initial ? 'Salvar' : 'Comentar'}</button>
      </div>
    </div>
  );
}

/** Comentários da tarefa. Quem for mencionado (e quem é responsável) recebe um aviso. */
export function Comments({ taskId }: { taskId: string }) {
  const ws = useWorkspace();
  const key = ['comments', taskId] as const;
  const list = useQuery({ queryKey: key, queryFn: () => api.fetchComments(taskId) });
  const add = useAction(api.addComment, [key]);
  const update = useAction(api.updateComment, [key]);
  const remove = useAction(api.deleteComment, [key]);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const items = list.data ?? [];
  return (
    <section className="cm" aria-label="Comentários">
      <h3>Comentários {items.length > 0 && <span>{items.length}</span>}</h3>
      {items.map(c => (
        <div className="cm-item" key={c.id}>
          <Avatar id={c.authorId} />
          <div className="cm-main">
            <span className="cm-head"><b>{ws.person(c.authorId).name}</b> <span className="muted">{fmtAgo(c.createdAt)}{c.editedAt ? ' · editado' : ''}</span></span>
            {editing === c.id
              ? <Composer label="Editar comentário" initial={c.body} initialMentions={c.mentions} onCancel={() => setEditing(null)}
                  onSubmit={async (b, m) => { await update(c.id, b, m); setEditing(null); }} />
              : <p className="cm-body"><Body c={c} /></p>}
            {editing !== c.id && (c.authorId === ws.me.id || ws.isAdmin) && (
              <span className="cm-tools">
                {c.authorId === ws.me.id && ws.canWrite && <button type="button" className="side-link inline" onClick={() => setEditing(c.id)}>Editar</button>}
                {confirm === c.id
                  ? <button type="button" className="side-link inline danger-txt" onClick={() => { setConfirm(null); void remove(c.id); }}>Confirmar exclusão</button>
                  : <button type="button" className="side-link inline" onClick={() => setConfirm(c.id)}>Excluir</button>}
              </span>
            )}
          </div>
        </div>
      ))}
      {!items.length && !ws.canWrite && <p className="muted">Nenhum comentário.</p>}
      {ws.canWrite && <Composer label="Novo comentário" onSubmit={(b, m) => add(taskId, b, m)} />}
    </section>
  );
}
